// Simple AABB collision + line-of-sight helpers used by both player and bots.
import * as THREE from "three";

/**
 * Push a moving cylinder (player/bot) out of overlapping walls (axis-aligned
 * boxes). Walls: [{ min: [x,y,z], max: [x,y,z] }].
 * Assumes uniform Y (we only care about XZ collisions).
 */
export function resolveCollisions(position, radius, walls) {
    let px = position.x;
    let pz = position.z;
    for (const w of walls) {
        const closestX = Math.max(w.min[0], Math.min(px, w.max[0]));
        const closestZ = Math.max(w.min[2], Math.min(pz, w.max[2]));
        const dx = px - closestX;
        const dz = pz - closestZ;
        const distSq = dx * dx + dz * dz;
        if (distSq < radius * radius) {
            const dist = Math.sqrt(distSq) || 0.0001;
            const push = radius - dist;
            px += (dx / dist) * push;
            pz += (dz / dist) * push;
        }
    }
    position.x = px;
    position.z = pz;
}

/**
 * Ray vs AABB list. Returns { t } if hit; smaller t = closer hit.
 */
export function raycastWalls(origin, dir, walls, maxDist = 100) {
    let closest = maxDist;
    let hit = false;
    for (const w of walls) {
        const t = rayAABB(origin, dir, w.min, w.max);
        if (t !== null && t < closest) {
            closest = t;
            hit = true;
        }
    }
    return hit ? { t: closest } : null;
}

function rayAABB(origin, dir, min, max) {
    let tmin = -Infinity, tmax = Infinity;
    for (let i = 0; i < 3; i++) {
        const o = i === 0 ? origin.x : i === 1 ? origin.y : origin.z;
        const d = i === 0 ? dir.x : i === 1 ? dir.y : dir.z;
        if (Math.abs(d) < 1e-6) {
            if (o < min[i] || o > max[i]) return null;
        } else {
            let t1 = (min[i] - o) / d;
            let t2 = (max[i] - o) / d;
            if (t1 > t2) [t1, t2] = [t2, t1];
            tmin = Math.max(tmin, t1);
            tmax = Math.min(tmax, t2);
            if (tmin > tmax) return null;
        }
    }
    return tmin > 0 ? tmin : (tmax > 0 ? tmax : null);
}

// Line-of-sight: is target visible from origin (no wall in between)?
export function hasLOS(originVec, targetVec, walls, maxDist) {
    const dir = new THREE.Vector3().subVectors(targetVec, originVec);
    const dist = dir.length();
    if (dist > maxDist) return false;
    dir.normalize();
    const hit = raycastWalls(originVec, dir, walls, dist);
    return !hit; // if hit before target, blocked
}
